/**
 * @typedef {Object} AppRoutingRouteSettings
 * @property {boolean} requiresAnonymous            Whether or not the user must not be
 *                                                  authenticated in order to access the
 *                                                  route (for example: The login out route).
 * @property {boolean} requiresAuthorization        Whether or not the user must be
 *                                                  authenticated in order to access the
 *                                                  route (for example: The logout route).
 * @property {Array}   requiresPermissions          A list of required permissions a the user
 *                                                  needs to have in order to access the route.
 * @property {boolean} requiresSwitchAccountEnabled Whether or not the user must be able to
 *                                                  switch between different accounts in order
 *                                                  to access the route.
 */

/**
 * A helper service to validate and work with the routes definition Olapic apps use.
 */
class AppRouting {
  /**
   * @param {AppSession}             appSession             To validate if a user is authenticated
   *                                                        when a routes requires it.
   * @param {AppSessionCapabilities} appSessionCapabilities To validate a user permissions when a
   *                                                        route requires it.
   */
  constructor(appSession, appSessionCapabilities) {
    'inject';

    /**
     * A local reference for the `appSession` service.
     * @type {AppSession}
     * @access protected
     */
    this._appSession = appSession;
    /**
     * A local reference for the `appSessionCapabilities` service.
     * @type {AppSessionCapabilities}
     * @access protected
     */
    this._appSessionCapabilities = appSessionCapabilities;
    /**
     * A dictionary with the default settings for routes.
     * @type {AppRoutingRouteSettings}
     * @access protected
     */
    this._routesDefaultSettings = {
      requiresAnonymous: false,
      requiresAuthorization: true,
      requiresPermissions: [],
      requiresSwitchAccountEnabled: false,
    };
  }
  /**
   * Extracts all the valid routes from a definition object.
   * @param {Object} definition A dictionary the app uses to store all the routes, by category.
   * @return {Array}
   */
  extractRoutes(definition) {
    return Object.keys(definition)
    .map((key) => this._getRoutesFromList(definition[key]))
    .reduce((currentList, list) => [...currentList, ...list], []);
  }
  /**
   * Validates whether a user can access or not a route.
   * @param {AppRoutingRouteSettings} [routeSettings={}]            The route custom settings.
   * @param {String}                  [authenticationRoute='login'] The route will be added on the
   *                                                                `redirectTo` property of the
   *                                                                return object if the user is
   *                                                                not authenticated and the
   *                                                                route requires it.
   * @param {String}                  [fallbackRoute='home']        The route will be added on the
   *                                                                `redirectTo` property of the
   *                                                                return object if the user is
   *                                                                not allowed to access the route.
   * @return {Object}
   * @property {boolean} allowed    Whether or not the user is allowed to access the route.
   * @property {boolean} denied     Whether or not the user is denied from accessing the route.
   * @property {?string} redirectTo A URL the user needs to be redirected in case the validation failed.
   */
  validate(
    routeSettings = {},
    authenticationRoute = 'login',
    fallbackRoute = 'home',
  ) {
    // Define the return object.
    const result = {
      allowed: false,
      denied: true,
      redirectTo: '',
    };

    // Create a new reference for the settings.
    const newSettings = Object.assign({}, routeSettings);
    // Merge the custom settings with the default ones.
    Object.keys(this._routesDefaultSettings).forEach((settingName) => {
      if (typeof newSettings[settingName] === 'undefined') {
        newSettings[settingName] = this._routesDefaultSettings[settingName];
      }
    });

    // Format the settings to avoid _"weird cases"_ (like `requiresAnonymous` with permissions).
    if (newSettings.requiresAnonymous) {
      newSettings.requiresAuthorization = false;
      newSettings.requiresPermissions = [];
    } else if (newSettings.requiresAuthorization) {
      newSettings.requiresAnonymous = false;
    }
    // Check if the user is authenticated.
    const isAuthenticated = this._appSession.isAuthenticated();
    if (newSettings.requiresAuthorization && !isAuthenticated) {
      /**
       * If the route requires authentication but the user is not logged in, set the redirection
       * to the authentication route.
       */
      result.redirectTo = authenticationRoute;
    } else if (
      (newSettings.requiresAnonymous && isAuthenticated) ||
      (
        newSettings.requiresPermissions &&
        !this._appSessionCapabilities.hasRoutePermissions(newSettings.requiresPermissions)
      ) ||
      (newSettings.requiresSwitchAccountEnabled && !this._appSession.canSwitchAccount())
    ) {
      /**
       * 1. If the route requires the user to not be authenticated and he/she is...
       * 2. Or if the route requires specific permissions and the user doesn't have them...
       * 3. Or if the route requires to be able to switch accounts and the user can't...
       *
       * Set the redirection to the fallback route.
       */
      result.redirectTo = fallbackRoute;
    } else {
      // If everything is ok, _allow_ the user to continue.
      result.allowed = true;
    }
    // Set the `denied` flag, just in case.
    result.denied = !result.allowed;
    return result;
  }
  /**
   * Helper method for `extractRoutes`. It recursively looks for nested routes and adds them on
   * a list.
   * @param  {Array} list A list of nested routes.
   * @return {Array}
   * @access protected
   */
  _getRoutesFromList(list) {
    const result = [];
    list.forEach((route) => {
      if (route.moduleId) {
        const newRoute = Object.assign({}, route);
        delete newRoute.items;
        result.push(newRoute);
      }
      if (route.items && route.items.length) {
        result.push(...this._getRoutesFromList(route.items));
      }
    });

    return result;
  }
}

export { AppRouting };
